import React, { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { getRandomStr, roundNum } from '../../../helpers';
import { dataConstants } from '../../../constants';
import TableToolbar from './TableSearch'
import EnhancedTableHead from './TableHead'
import TablePaginationActions from './TablePagination'
import { formatCount } from '../../../utils/D3/helper';
import SearchOffRoundedIcon from '@mui/icons-material/SearchOffRounded';
import { formatTableValue } from '../../../utils';
import { Box } from '@mui/system';
import NorthEastRoundedIcon from '@mui/icons-material/NorthEastRounded';
import { Checkbox, Menu, Skeleton } from '@mui/material';

// ------- watchlist -------

import { Link, } from '@mui/material';
import getWatchlist from "../../../../lib/watchlist/_getWatchlist";
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import AddToWatchlistMenu from '../../_common/AddToWatchlist';

const descendingComparator = (a, b, orderBy) => {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

const getComparator = (order, orderBy) => {
  return order === 'asc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

const stableSort = (array, comparator) => {
  if (!array || array.length === 0) return [];
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.primary.main,
    fontWeight: 600,
    // color: 'rgba(0, 0, 0, 0.87)',
    color: '#fff',
    boxShadow: theme.shadows[1],
    fontSize: 11,
  },
}));

const escapeRegExp = (value) => {
  return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
}

const useStyles = () => ({
  root: undefined,
  table: undefined,
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: '1px',
    margin: '-1px',
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: '20px',
    width: '1px',
  },
  tableHeaderCell: undefined,
});

const StyledTable = styled(Table)(({ theme }) => ({
  '& .shortName.screener-field-ellipsis': {
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    width: '8rem',
    display: 'block',
    overflow: 'hidden',
  },

  '& .industryName-ellipsis': {
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    width: '6.5rem',
    display: 'block',
    overflow: 'hidden',
  },

  '& .shortName-th ': {
    width: '7rem',
  },

  '& a': {
    textDecoration: 'none',
    color: '#161b57'
  },

  '& a:hover': {
    textDecoration: 'underline',
  },

  '& .industryName-th': {
    width: '6.5rem',
  },

  '& .MuiTableSortLabel-icon': {
    margin: '0 !important',
  },

  '& .MuiTableCell-sizeSmall': {
    fontSize: '.84rem',
    padding: '6px 12px',
    fontVariantNumeric: 'tabular-nums',
  },

  '& .row-selected td': {
    fontWeight: '700 !important',
  },

  '& .MuiTableCell-root.tickername, .MuiTableCell-root .industry-ellipsis, .MuiTableCell-root .sector-ellipsis': {
    maxWidth: '100px',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
  },

  '& .MuiTableCell-root.ticker': {
    width: '77px',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
  },

  '& thead tr': {
    textAlign: 'center !important',
  },

  '& thead tr .mainPeersHeader': {
    borderRight: '1px solid rgba(0,0,0,0.06)',
    borderTop: '1px solid rgba(0,0,0,0.06)',
    borderBottom: '1px solid rgba(0,0,0,0.06)',
  },

  '& thead tr .mainPeersHeader:last-child': {
    borderRight: '0',
    // borderTop: '0',
  },

  '& thead tr .mainPeersHeader:first-child ': {
    // borderTop: '0',
  },

  '& thead tr th:nth-child(2), tbody tr td:nth-child(2)': {
    position: 'sticky',
    left: '0',
    zIndex: '4',
    background: '#fff',
  },

  '& thead tr th': {
    background: '#fff',
  },

  [theme.breakpoints.up("sm")]: {
    '& thead tr .mainPeersHeader:first-child ': {
      zIndex: '0',
    },

    '& thead tr th:nth-child(2), tbody tr td:nth-child(2)': {
      position: 'sticky',
      left: '0',
      zIndex: '4',
      background: '#fff',
    },

    '& thead tr th:nth-child(2)': {
      zIndex: '3',
    },

    '& thead tr th': {
      zIndex: '1',
      background: '#fff',
      color: '#5a5e73',
      fontSize: '0.72rem',
      fontWeight: '700 !important',
      textTransform: 'uppercase',
      letterSpacing: '0.04em',
      borderBottom: '1px solid rgba(0,0,0,0.1) !important',
    },
    '& .deleteWatchlistItem .deleteRow': {
      color: '#999'
    },
    '& tbody tr:hover .deleteWatchlistItem .deleteRow': {
      color: 'hsl(235deg 59.62% 21.27%)' /*#020B4A*/
    },
    '& .MuiTypography-caption': {
      fontSize: '.875rem'
    }
  },
}));

const EnhancedTable = ({
  wrapperClass,
  shortWrapperClass,
  tableClass,
  headCells,
  defaultHeadCells,
  rows,
  noOfRows,
  quickSearch,
  fullToolbarAccess,
  createSections,
  needToolbar,
  fieldToSort,
  handleLinkClick,
  mainHeadCols,
  history,
  maxHeight,
  isLoading,
  searchPosAbsolute,
  estimatedResults,
  setOffset, offset, mounted,
  menu, tableName, setTableCols, manageColumns, saveScreener,
  download, results, isPremium, setBanner, user, _watchlists,
  activePresetName,
}) => {
  const classes = useStyles();
  const sortOrderBy = headCells && headCells.length ? headCells.find(d => d?.class === 'sorted') : '';
  const sortOrderByField = sortOrderBy && sortOrderBy ? sortOrderBy.field : '';
  const [screenerTableRows, setScreenerTableRows] = React.useState(rows);
  const [screenerTableCols, setScreenerTableCols] = React.useState(defaultHeadCells);

  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState(sortOrderBy ? sortOrderByField : '');
  const [selected, setSelected] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(noOfRows ? parseInt(noOfRows) : (rows && rows.length ? rows.length : 25));
  const [searchText, setSearchText] = React.useState("");
  const rowsPerPageOptions = rows?.length <= 30 ? [10, { value: -1, label: 'All' }] : [10, 25, 50, { value: -1, label: 'All' }];

  const [watchlists, setWatchlists] = useState([]);
  const [_ticker, setTicker] = useState("");
  const [contextMenu, setContextMenu] = React.useState(null);

  useEffect(() => {
    setScreenerTableCols(defaultHeadCells)
  }, [defaultHeadCells])

  const onReorderEnd = React.useCallback(
    ({ oldIndex, newIndex, collection, isKeySorting }, e) => {
      const newOrder = [...screenerTableCols];
      const moved = newOrder.splice(oldIndex, 1);
      newOrder.splice(newIndex, 0, moved[0]);
      setScreenerTableCols(newOrder);
      setTableCols(newOrder)
    },
    [screenerTableCols, setScreenerTableCols, setTableCols]
  );

  const requestSearch = (searchValue) => {
    setPage(0);
    setSearchText(searchValue);
    const searchRegex = new RegExp(escapeRegExp(searchValue), "i");
    const filteredRows = rows.filter((row) => {
      return Object.keys(row).some((field) => {
        return row[field] ? searchRegex.test(row[field].toString()) : null;
      });
    });
    setScreenerTableRows(filteredRows);
  };

  React.useEffect(() => {
    setScreenerTableRows(rows);
  }, [rows]);

  const followedTickers = React.useMemo(() => {
    const set = new Set();
    watchlists?.forEach((item) => {
      try {
        const tickers = JSON.parse(item.tickers);
        if (Array.isArray(tickers)) tickers.forEach(t => set.add(t));
      } catch { }
    });
    return set;
  }, [watchlists]);

  const handleClick = (event, sortOrderByField) => {
    const selectedIndex = selected?.indexOf(sortOrderByField);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected?.concat(selected, sortOrderByField);
    } else if (selectedIndex === 0) {
      newSelected = newSelected?.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected?.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected?.concat(
        selected?.slice(0, selectedIndex),
        selected?.slice(selectedIndex + 1),
      );
    }

    setSelected(newSelected);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = rows?.map((n) => n.ticker);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };


  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const onLinkClick = (link, tabCode) => {
    if (link) {
      history.push(link, history.location.pathname)
      tabCode && handleLinkClick(dataConstants.STOCK_TABS.SUM)
    }
    window.location.reload()
  }

  const handleContextMenu = (event, ticker) => {
    setTicker(ticker)
    // event.preventDefault();
    setContextMenu(
      contextMenu === null
        ? {
          mouseX: event.clientX + 2,
          mouseY: event.clientY - 6,
        }
        : // repeated contextmenu when it is already open closes it with Chrome 84 on Ubuntu
        // Other native context menus might behave different.
        // With this behavior we prevent contextmenu from the backdrop to re-locale existing context menus.
        null,
    );
  };

  const handleClose = () => {
    setContextMenu(null);
  };

  // useEffect(() => {
  //   if (watchlists?.length === 0) { setWatchlists(_watchlists) }
  // }, [_watchlists])

  const _get_watchlist_ = async (accessToken) => {
    if (accessToken) {
      await getWatchlist(accessToken).then(data => {
        // const { getWatchlist } = data || {}
        return setWatchlists(data?.getWatchlist ?? [])
      });
    }
  }

  useEffect(() => {
    _get_watchlist_(user?.accessToken)
  }, [user?.accessToken])

  return (
    <Box component="div" sx={{ bgcolor: '#fff', boxShadow: 0, pt: 1, width: '100%' }}>
      {quickSearch &&
        <TableToolbar
          value={searchText}
          onChange={(event) => requestSearch(event.target.value)}
          clearSearch={() => requestSearch('')}
          searchPosAbsolute={searchPosAbsolute}
          results={results}
          menu={menu}
          tableName={tableName}
          manageColumns={manageColumns}
          saveScreener={saveScreener}
          download={download}
          pagination={needToolbar ?
            fullToolbarAccess ?
              <TablePagination
                rowsPerPageOptions={screenerTableRows?.length >= rowsPerPage ? rowsPerPageOptions : [10]}
                component="div"
                className="pagination-toolbar"
                colSpan={3}
                count={screenerTableRows?.length}
                rowsPerPage={rowsPerPage}
                page={page}
                SelectProps={{
                  inputProps: { 'aria-label': 'rows per page' },
                  native: true,
                }}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={(props) => <TablePaginationActions {...props} setOffset={setOffset} offset={offset} estimatedResults={estimatedResults} activePresetName={activePresetName} />}
              /> :
              <TablePagination
                sx={{
                  color: 'rgb(67, 65, 102)',
                  '& .MuiTablePagination-selectLabel, .MuiInputBase-root.MuiInputBase-colorPrimary': { display: 'none' },
                  '& .table-pagination': { ml: { md: '20px', xs: '6px', sm: '2px' } }
                }}
                // rowsPerPageOptions={screenerTableRows?.length >= rowsPerPage ? rowsPerPageOptions : [10]}
                rowsPerPageOptions={!isPremium ? [25] : [10, 25, 50, 100]}
                component="div"
                className="pagination-toolbar"
                colSpan={3}
                count={screenerTableRows?.length}
                rowsPerPage={rowsPerPage}
                page={page}

                // labelDisplayedRows={function defaultLabelDisplayedRows({ from, to, count }) {
                //   const totalPages = Math.ceil(estimatedResults / rowsPerPage);// Calculate the total number of pages
                //   const startResult = page * rowsPerPage + 1;// Calculate the range of results currently displayed
                //   const endResult = Math.min((page + 1) * rowsPerPage, estimatedResults);
                //   return `Showing ${startResult} – ${endResult} of ${estimatedResults} results: Page ${page + 1} of ${totalPages}`;
                //   // return `Showing ${from} – ${to} of ${count !== -1 ? `${count} result${count <= 1 ? '' : 's'}` : `more than ${to}`}`;
                // }}

                labelDisplayedRows={({ from, to, count }) => {
                  const totalPages = Math.ceil(estimatedResults / rowsPerPage);
                  const currentPage = page + 1;
                  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 600;
                  if (isMobile) {
                    return `Showing results: ${from} – ${to} of ${count !== -1 ? count : `more than ${to}`}`;
                  }
                  return `Showing ${from} – ${to} of ${count !== -1 ? count : `more than ${to}`} results: Page ${currentPage} of ${totalPages}`;
                }}

                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={(props) => <TablePaginationActions {...props} setOffset={setOffset} offset={offset} estimatedResults={estimatedResults} setPage={setPage} isPremium={isPremium} setBanner={setBanner} isLoading={isLoading} activePresetName={activePresetName} />}
                SelectProps={{
                  inputProps: { 'aria-label': 'rows per page' },
                  native: true,
                }}
              />
            : null}
        />}

      <TableContainer className={`${wrapperClass}-container`} sx={{ maxHeight: maxHeight || '' }}>
        <StyledTable
          // #434166
          sx={{ '& td': { fontSize: '.84rem', color: '#1d1d2e', fontVariantNumeric: 'tabular-nums' }, '& .MuiTableCell-sizeSmall.sorted': { bgcolor: 'rgba(22,27,87,.04) !important' } }}
          className={`${`${wrapperClass}-tbl` || ''} ${`${tableClass}` || ''}`}
          size="small"
          aria-label="a dense table"
          // aria-label="sticky table"
          stickyHeader
          // style={{ minWidth: 0 }}
          // aria-label="sticky table"
          id="screener-table"
        >
          <EnhancedTableHead
            classes={classes}
            wrapperClass={wrapperClass}
            numSelected={selected?.length}
            order={order}
            orderBy={orderBy}
            onSelectAllClick={handleSelectAllClick}
            onRequestSort={handleRequestSort}
            rowCount={rows?.length}
            headCells={headCells}
            value={searchText}
            onChange={(event) => requestSearch(event.target.value)}
            clearSearch={() => requestSearch('')}
            handleSortedCells={() => { }}
            mainHeadCols={mainHeadCols}
            onReorderEnd={onReorderEnd}
            screenerTableCols={screenerTableCols}
          />
          <TableBody>
            {/* if you don't need to support IE11, you can replace the `stableSort` call with:
              rows.slice().sort(getComparator(order, orderBy)) */}
            {isLoading && Array(10).fill()?.map((_, skIdx) => (
              <TableRow hover key={`skeleton-row-${skIdx}`} sx={{ bgcolor: '#fff', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                {headCells.map((hc, hcIdx) => <TableCell key={`skeleton-cell-${skIdx}-${hcIdx}`} component="td" scope="row"><Skeleton animation="wave" /></TableCell>)}
              </TableRow>))}
            {!createSections && !isLoading &&
              stableSort(screenerTableRows, getComparator(order, orderBy))
                ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                ?.map((row, index) => {
                  const rowID = `enhanced-table-${index}`;
                  const label = `add-to-watchlist-${index}`;
                  const selectRow = row['activeRow'] ? 'row-selected' : '';
                  const ticker = row['ticker'];
                  const following = followedTickers.has(ticker);
                  return (
                    <TableRow
                      hover
                      // onClick={(event) => handleClick(event, row[sortOrderByField])}
                      tabIndex={-1}
                      key={`row-${rowID}-${ticker || index}`}
                      className={`${selectRow || ''}`}
                      sx={{
                        bgcolor: '#fff',
                        borderBottom: '1px solid rgba(0,0,0,0.06)',
                        transition: 'background-color 0.15s ease',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.015)' },
                        '&:hover td': { bgcolor: 'transparent' },
                      }}
                      role="checkbox"
                      aria-checked={following}
                    >
                      <LightTooltip title={following ? `` : `Add to watchlist`} placement="bottom-end" followCursor disableInteractive>
                        <TableCell padding="checkbox" sx={{ background: '#fff' }}>
                          <Checkbox
                            aria-describedby={"enhanced-table"}
                            sx={{ color: 'rgb(128,134,139)', '&:hover, &:hover.Mui-checked': { color: '#161b57' }, '&.Mui-checked': { color: 'rgb(128,134,139)' } }}
                            size="small"
                            checked={following}
                            onClick={(e) => handleContextMenu(e, ticker)}
                            icon={<AddCircleOutlineRoundedIcon />} checkedIcon={<CheckCircleRoundedIcon />}
                            inputProps={{
                              'aria-labelledby': label,
                            }}
                          />
                        </TableCell>
                      </LightTooltip>
                      {screenerTableCols && screenerTableCols.length > 0 && screenerTableCols.map((c, cIdx) => {
                        if (!c) return null;
                        // if (c?.isPremium && !hasPremiumAccess(login, isLoginRedirect)) {
                        if (c?.isPremium && !isPremium) {
                          return (
                            <TableCell key={`cell-${rowID}-${c.field || 'premium'}`} className={`${c.class === 'sorted' ? '' : (c.class || '')} font-weight-bold text-left`} component="td" id={`td-${rowID}-${c.field || 'premium'}`} >
                              <Link target="_blank" href={`${dataConstants.NAVS.PRICING}`} sx={{ color: '#161b57 !important' }}>
                                Premium
                              </Link>
                            </TableCell>)
                        } else {
                          if (c?.isPct) {
                            const classField = c?.field ? c?.field.replace(/[^\w\s]/gi, '') : '';
                            const _pctVal = row[c.field] ? (c.mult ? row[c.field] * c.mult : row[c.field]) : 0;
                            const _pctAlpha = Math.min(0.15, Math.abs(_pctVal) / 100);
                            const _heatBg = _pctVal > 0 ? `rgba(16,185,129,${_pctAlpha})` : _pctVal < 0 ? `rgba(239,68,68,${_pctAlpha})` : 'transparent';
                            return (
                              c?.isPct && c?.tooltip ?
                                <LightTooltip key={`cell-${rowID}-${c.field || cIdx}`} title={c?.tooltip} placement="bottom-end" followCursor disableInteractive>
                                  <TableCell sx={{ backgroundColor: _heatBg }} className={` text-uppercase ${classField === orderBy ? 'sorted' : ''} ${classField || ''}`} component="td" id={`td-${rowID}-${c.field || cIdx}`} scope="row" >
                                    <Typography variant='caption' sx={{ color: (row[c.field] * 100) < 0 ? '#D82748' : 'inherit' }}> {c.mult ? (row[c?.field] ? `${roundNum(row[c?.field] * c.mult)}%` || '' : '-') : (row[c?.field] ? `${roundNum(row[c?.field])}%` || '' : '-')}</Typography>
                                  </TableCell>
                                </LightTooltip>
                                :
                                <TableCell key={`cell-${rowID}-${c.field || cIdx}`} sx={{ backgroundColor: _heatBg }} className={` text-uppercase ${classField === orderBy ? 'sorted' : ''} ${classField || ''}`} component="td" id={`td-${rowID}-${c.field || cIdx}`} scope="row" >
                                  <Typography variant='caption' sx={{ color: (row[c.field] * 100) < 0 ? '#D82748' : 'inherit' }}> {c?.mult ? (row[c?.field] ? `${roundNum(row[c?.field] * c.mult)}%` || '' : '-') : (row[c?.field] ? `${roundNum(row[c?.field])}%` || '' : '-')}</Typography>
                                </TableCell>
                            )
                          } else if (c?.urlField) {
                            const classField = c?.field ? c?.field.replace(/[^\w\s]/gi, '') : '';
                            return (
                              <LightTooltip key={`cell-${rowID}-${c.field || cIdx}`} title={row[c?.field]} placement="bottom-end" followCursor disableInteractive>
                                <TableCell
                                  sx={{ '&:hover .blank': { display: 'inline' }, '& .tickername.screener-field-ellipsis, .screener-field-ellipsis': { whiteSpace: 'nowrap', textOverflow: 'ellipsis', display: 'block', overflow: 'hidden', color: 'text.link', cursor: 'pointer' }, '&.ticker': { bgcolor: 'background.primary_light_' }, bgcolor: '#fff' }}
                                  // title={formatTableValue(row[c?.field]) || ''}
                                  className={`${c?.class || ''} text-uppercase ${classField === orderBy ? 'sorted' : ''} ${classField || ''}`} component="td" id={`td-${rowID}-${c.field || cIdx}`} scope="row"
                                >
                                  <a target="_blank" href={c?.urlField === 'ticker' ? `${dataConstants.NAVS.STOCK}/${row[c?.urlField]}` : `${dataConstants.NAVS.FUNDS}/${row[c?.urlField]}`} style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                                    <Typography variant="caption" className={`${shortWrapperClass}-ellipsis ${c?.field}`}>
                                      {row[c?.field] ? formatTableValue(row[c?.field]) : '-'}
                                    </Typography>
                                    <NorthEastRoundedIcon className="blank" fontSize='small' sx={{ color: '#161b57', fontSize: '.9rem', position: 'absolute', right: 0, background: '#fff', display: 'none' }} />
                                  </a>
                                </TableCell>
                              </LightTooltip>)
                          } else if (c?.peerValue || c?.isSectorValue) {
                            const classField = c?.field ? c?.field.replace(/[^\w\s]/gi, '') : '';
                            return (
                              <TableCell key={`cell-${rowID}-${c.field || cIdx}`}
                                // title={formatTableValue(row[c?.field]) || ''}
                                className={` text-uppercase ${classField === orderBy ? 'sorted' : ''} ${classField || ''}`} component="td" id={`td-${rowID}-${c.field || cIdx}`} scope="row" >
                                {(c?.peerValue || c?.isSectorValue) && c?.tooltip ?
                                  <LightTooltip title={c?.tooltip} placement="bottom-end" followCursor disableInteractive>
                                    <Typography variant="caption" component="div" className={`${c?.field}-ellipsis text-nowrap`}>
                                      {/* {row[c?.field] ? formatCount(Math.sign(row[c?.field]) * row[c?.field], true, 1, Math.sign(row[c?.field])) : '-'} */}
                                      {row[c?.field] ? formatCount(Math.sign(row[c?.field]) * row[c?.field], true, 1, Math.sign(row[c?.field])) : '-'}
                                      {/* {row[c?.field] && formatCount(Math.sign(row[c?.field]) * row[c?.field], true, 1, Math.sign(row[c?.field]))} */}
                                    </Typography>
                                  </LightTooltip>
                                  :
                                  <Typography variant="caption" component="div" className={`${c?.field}-ellipsis text-nowrap ${c?.field}`}>
                                    {row[c?.field] ? formatCount(Math.sign(row[c?.field]) * row[c?.field], true, 1, Math.sign(row[c?.field])) : '-'}
                                  </Typography>}
                              </TableCell>)
                          } else if (c?.field?.toLowerCase() === 'industry' || c?.field?.toLowerCase() === 'sector') {
                            const classField = c?.field ? c?.field.replace(/[^\w\s]/gi, '') : '';
                            return (
                              <TableCell key={`cell-${rowID}-${c.field || cIdx}`}
                                className={` text-uppercase ${classField === orderBy ? 'sorted' : ''} ${classField || ''}`} component="td" id={`td-${rowID}-${c.field || cIdx}`} scope="row" >
                                <LightTooltip title={row[c?.field]} placement="bottom-end" followCursor disableInteractive>
                                  <Typography variant="caption" component="div" className={`${c?.field}-ellipsis text-nowrap`}>
                                    {row[c?.field] ? row[c?.field] : '-'}
                                  </Typography>
                                </LightTooltip>
                              </TableCell>)
                          } else {
                            const classField = c?.field ? c?.field.replace(/[^\w\s]/gi, '') : '';
                            return (
                              <TableCell key={`cell-${rowID}-${c.field || cIdx}`}
                                // title={formatTableValue(row[c?.field]) || ''}
                                className={` text-uppercase ${classField === orderBy ? 'sorted' : ''} ${classField || ''}`} component="td" id={rowID} scope="row" >
                                <Typography variant="caption" component="div" className={`${c?.field}-ellipsis text-nowrap ${c?.field}`}>
                                  {row[c?.field] ? formatTableValue(row[c?.field]) : '-'}
                                </Typography>
                              </TableCell>)
                          }
                        }
                      })
                      }
                    </TableRow>
                  )
                })
            }
            {/* {isLoading &&
              <TableRow className="w-100" sx={{ height: '15rem' }}>
                <TableCell key={getRandomStr(3)} colSpan={headCells?.length > defaultHeadCells?.length ? headCells?.length : defaultHeadCells?.length} className="text-center border-0 loading">
                  <GrufityCircularProgress />
                </TableCell>
              </TableRow>} */}
            {/* {screenerTableRows?.length <= 0 && !mounted &&
              <TableRow sx={{ height: '15rem' }}>
                <TableCell key={getRandomStr(3)} sx={{ textAlign: 'center' }} colSpan={headCells?.length > defaultHeadCells?.length ? headCells?.length : defaultHeadCells?.length} className="text-center border-0 no-data">
                  <p className="mt-2" style={{ color: '#777', fontSize: '.875rem' }}>Loading...</p>
                </TableCell>
              </TableRow>} */}
            {screenerTableRows?.length <= 0 && !isLoading && mounted &&
              <TableRow sx={{ height: '15rem' }}>
                <TableCell key="empty-state" sx={{ textAlign: 'center' }} colSpan={headCells?.length > defaultHeadCells?.length ? headCells?.length : defaultHeadCells?.length} className="text-center border-0 no-data">
                  <SearchOffRoundedIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 600 }}>No companies match these criteria</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>Try broadening your filters or removing some conditions</Typography>
                </TableCell>
              </TableRow>}
            {/* {emptyRows > 0 && needToolbar && (
              <TableRow style={{ height: (dense ? 25 : 25) * emptyRows }}>
                <TableCell key={getRandomStr(3)} colSpan={headCells?.length > defaultHeadCells?.length ? headCells?.length : defaultHeadCells?.length} />
              </TableRow>
            )} */}
          </TableBody>
        </StyledTable>
      </TableContainer>
      <TablePagination
        sx={{ color: 'rgb(67, 65, 102)', '& .MuiTablePagination-displayedRows, .MuiTablePagination-actions': { display: 'none' }, pointerEvents: 'none' }}
        rowsPerPageOptions={!isPremium ? [25] : [10, 25, 50, 100]}
        component="div"
        className="pagination-toolbar"
        colSpan={3}
        count={screenerTableRows?.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        SelectProps={{
          inputProps: { 'aria-label': 'rows per page' },
          native: true,
        }}
      />
      <Menu
        open={contextMenu !== null}
        onClose={handleClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        sx={{ '& .MuiList-padding.MuiMenu-list': { py: 0 } }}
      >
        <AddToWatchlistMenu
          ticker={_ticker}
          _watchlists={watchlists}
          setWatchlists={setWatchlists}
        />
      </Menu>
    </Box >
  );
}

export default React.memo(EnhancedTable);;
